import { useTranslation } from 'react-i18next';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { formatInterestRate } from '@/localization/percentages';

const today = new Date().toLocaleDateString('sv-SE', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

type Payload = {
  email: string;
  nationalNumber: string;
  payoutAmount: number;
  loanAmount: number;
  interestRate: number;
  setupFee: number;
  installmentFee: number;
  installmentAmount: number;
  numberOfInstallments: number;
  totalCost: number;
  effectiveInterestRate: number;
  totalRepaid: number;
};
type Props = ModalProps<Payload>;

const LoanIdProtectAgreement = ({
  payload: {
    email,
    nationalNumber,
    payoutAmount,
    loanAmount,
    interestRate,
    setupFee,
    installmentFee,
    installmentAmount,
    numberOfInstallments,
    totalCost,
    effectiveInterestRate,
    totalRepaid,
  },
}: Props) => {
  const { t } = useTranslation('Common');

  return (
    <Modal title="Kreditavtal Fairlo Trygglån">
      <div className="flex flex-col gap-4">
        <p className="text-body-semibold">Förmedlad av Fairlo AB (Fairlo)</p>
        <p>{today}</p>
        <p>Avtalsnummer: </p>

        <h2 className="text-section-util">1. Allmän information</h2>

        <p>
          Kreditgivare är Bankaktiebolaget Nordiska (publ), organisationsnummer 556760-6032, Box 173, 101 23
          Stockholm.
        </p>

        <p>Kreditförmedlare är Fairlo AB, organisationsnummer 559084-1101, Box 111 10, 100 61 Stockholm.</p>

        <p>
          Kreditgivaren har givit Fairlo AB, i uppdrag att administrera Krediten för Kreditgivarens räkning,
          varför Kredittagaren fortsatt kan vända sig till Fairlo AB med eventuella frågor till dess
          Kreditgivaren meddelar annorlunda.
        </p>

        <p>
          Detta är Kredittagarens Kreditavtal för Fairlo Trygglån. Avtalet har ingåtts mellan Kredittagaren
          och Kreditgivaren. Nedan framgår de specifika egenskaperna och villkoren för Krediten. För övriga
          villkor och bestämmelser gäller Fairlos Allmänna villkor samt den Betalningsplan som vid varje
          enskild tidpunkt gäller för Krediten och som framgår av Kredittagarens Mina sidor på www.fairlo.se.
          Vid signering av detta Kreditavtal godkänner Kredittagaren i) att Fairlos Allmänna villkor är en del
          av Kreditavtalet och att Kredittagaren tagit del av och godkänt dessa och ii) att den Betalningsplan
          som vid varje enskild tidpunkt godkänts av Kredittagaren i inloggat läge med BankID på www.fairlo.se
          är en del av kreditavtalet.
        </p>

        <h2 className="text-section-util">2. Parter</h2>

        <div>
          <p className="text-body-semibold">Kredittagare</p>
          <span data-private>{email}</span>
          <p>
            Personnummer:
            <span data-private>{nationalNumber}</span>
          </p>
        </div>

        <div>
          <p className="text-body-semibold">Kreditgivare</p>
          <p>Bankaktiebolaget Nordiska (publ)</p>
          <p>Box 173</p>
          <p>101 23 Stockholm</p>
        </div>

        <p>Organisationsnummer: 556760-6032</p>

        <h2 className="text-section-util">3. Kredit</h2>

        <dl className="[&>dt]:text-body-semibold [&>dd]:mb-2 [&>dd]:ml-0 [&>dt]:mt-4 [&>dt]:mb-1 [&>dt:first-child]:mt-0">
          <dt>Typ av kredit</dt>
          <dd>Kredit utan säkerhet i form av ett annuitetslån.</dd>

          <dt>Nyttjande</dt>
          <dd>
            {t('money', { amount: payoutAmount })} av Krediten utbetalas till det bankkonto som Kredittagaren
            har angivit och anslutit. {t('money', { amount: loanAmount - payoutAmount })} utbetalas till
            Kreditförmedlaren för betalning av det anknytande avtalet Fairlo Personskydd samt
            uppläggningsavgift.
          </dd>

          <dt>Återbetalning</dt>
          <dd>Månadsbelopp i enlighet med annuitetsplan.</dd>

          <dt>Beviljad kredit</dt>
          <dd>{t('money', { amount: loanAmount })}</dd>

          <dt>Ränta och avgifter</dt>
          <dd>
            Ränta utgår med en årlig räntesats om {formatInterestRate(interestRate)}.
            <br />
            Vid utbetalningen av Krediten tillkommer en uppläggningsavgift: {t('money', { amount: setupFee })}
            <br />
            och månadsavgift (varje faktura): {t('money', { amount: installmentFee })}
          </dd>

          <dt>Månadsbelopp</dt>
          <dd>
            {t('money', { amount: installmentAmount })} per månad. Hur mycket Kredittagaren ska betala varje
            månad framgår av den senast avtalade Betalningsplanen i enlighet med Fairlos Allmänna villkor.
          </dd>

          <dt>Löptid</dt>
          <dd>{numberOfInstallments} månader</dd>

          <dt>Total Kostnad</dt>
          <dd>Den totala kostnaden för Krediten uppgår till {t('money', { amount: totalCost })}</dd>

          <dt>Effektiv ränta</dt>
          <dd>{formatInterestRate(effectiveInterestRate)}</dd>

          <dt>Totalt belopp att återbetala</dt>
          <dd>
            Det sammanlagda belopp Kredittagaren kommer att betala uppgår till{' '}
            {t('money', { amount: totalRepaid })}, om Kredittagaren betalar i enlighet med Betalningsplanen
            som gäller när krediten utbetalas.
          </dd>

          <dt>Ändring av ränta och avgifter</dt>
          <dd>
            Kreditgivaren har rätt att ändra priserna i prisbilagan i enlighet med vad som framgår av de
            Allmänna villkoren. Kreditgivaren äger när som helst under kreditperioden rätt att besluta om
            höjning av särskild avgift i den mån Kreditgivarens kostnader ökat för den åtgärd avgiften avser
            att täcka. Kreditgivaren tillämpar ovanstående villkor om ändring av avgift även till
            Kredittagarens förmån och sänker således avgiften i den mån Kreditgivarens kostnader för den
            åtgärd avgiften avser att täcka minskat.
          </dd>
        </dl>

        <h2 className="text-section-util">4. Signering</h2>
        <p>
          Detta avtal har upprättats elektroniskt och signerats med personligt BankID av Kredittagaren.
          Kredittagaren har mottagit en kopia av detta Kreditavtal och de Allmänna villkoren via e-post till
          den e-postadress som Kredittagaren angivit i sin ansökan om kredit.
        </p>
      </div>
    </Modal>
  );
};

export default LoanIdProtectAgreement;
